import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useTwinStore } from "./twinManagement";
import { useChangelogsStore } from "./changelogs";

export const useTwinEditCreateStore = defineStore("twinCreateEdit", () => {
  const twinStore = useTwinStore();

  // if true, the asset already exists and is being edited, if false, a new asset is being created
  const isEditing = ref(false);

  // currently edited inputs from the user. The type is { <propertyUuid>: <propertyValue> }
  const inputs = ref<Record<string, any>>({});

  const name = ref<string | null>(null);
  const description = ref<string | null>(null);

  // Merge of the user inputs and the current property values
  const mergedInputs = computed(() => ({
    ...propertyObject.value,
    ...inputs.value,
  }));

  // The asset that is being edited is either the template, if creating; or the asset itself, if editing
  const asset = computed(() =>
    isEditing.value
      ? twinStore.twinLoadingState.result
      : twinStore.templateLoadingState.result,
  );

  const assetTemplate = computed(() => twinStore.templateLoadingState.result);

  const propertyObject = computed<Record<string, unknown>>(
    () =>
      asset.value?.properties!.reduce(
        (agg, curr) => {
          agg[curr.uuid] = curr.propertyValue?.value;
          return agg;
        },
        {} as Record<string, unknown>,
      ) ?? {},
  );

  // const propValuesByName = computed(() =>
  //   asset.value?.properties.reduce((agg, curr) => {
  //     agg[curr.name] = curr.propertyValue?.value;
  //     return agg;
  //   }, {})
  // )

  const propValuesByCustomId = computed(() =>
    asset.value?.properties.reduce((agg, curr) => {
      // NOTE customId is unforunately not mandatory and can be undefined
      if (curr.customId) {
        agg[curr.customId] = curr.propertyValue?.value;
      }
      return agg;
    }, {}),
  );

  // Get current property value by name
  function getPropByName(name: string) {
    return asset.value?.properties.find((item) => item.name === "name")
      ?.propertyValue?.value;
  }

  // init requires a template to know which properties should be available,
  // as well as an optional parameter id, if id is set, init will load the asset, and set the mode to editing
  async function init(templateId?: string, id?: string) {
    if (templateId === undefined && id === undefined) {
      throw new Error("Either templateId or id must be provided");
    }
    // Reset all data before loading
    inputs.value = {};
    name.value = null;
    description.value = null;
    if (id) {
      isEditing.value = true;
      const twin = await twinStore.loadTwin(id, true);
      await twinStore.loadTemplate(twin.templateUuid, false);
    } else {
      isEditing.value = false;
      await twinStore.loadTemplate(templateId!, false);
      // Name would be the name of the template, which might be confusing, see https://github.com/inctec/smx-portal/issues/524
      name.value = "";
    }
  }

  function resetData() {
    name.value = null;
    description.value = null;
    inputs.value = {};
  }

  function endEditing() {
    resetData();
    isEditing.value = false;
  }

  const changelogsStore = useChangelogsStore();

  async function savePropertyByCustomId(
    customId: string,
    value: any,
    comment: string,
    longComment: string,
  ) {
    const propertyUuid = asset.value?.properties.find(
      (item) => item.customId === customId,
    )?.uuid;

    if (propertyUuid === undefined) {
      throw new Error("Try to set non existing property " + customId);
    }

    await twinStore.editTwin(asset.value, {
      comment,
      longComment,
      properties: [
        {
          propertyUuid,
          value,
        },
      ],
    });
    changelogsStore.reloadChangelogs();
  }

  const propIdsByCustomId = computed(() =>
    asset.value?.properties.reduce((agg, curr) => {
      // NOTE: unfortunately customId can be null
      if (curr.customId) {
        agg[curr.customId] = curr.uuid;
      }
      return agg;
    }, {}),
  );

  return {
    init,
    endEditing,
    resetData,
    asset,
    assetTemplate,
    inputs,
    name,
    description,
    isEditing,
    mergedInputs,
    savePropertyByCustomId,
    propertyObject,
    propValuesByCustomId,
    propIdsByCustomId,
  };
});
